import { useMemo } from 'react';
import { useSimulatorStore } from '../../stores/useSimulatorStore';
import { InfoButton } from '../common/Tooltip';
import type { PackageConfig } from '../../types';

interface MapPoint {
  label: string;
  x: number;
  y: number;
  type: 'package' | 'competitor';
  color?: string;
}

const COMPETITORS: MapPoint[] = [
  { label: 'Fitbit', x: 22, y: 18, type: 'competitor' },
  { label: 'Apple Watch', x: 38, y: 28, type: 'competitor' },
  { label: 'Peloton', x: 28, y: 42, type: 'competitor' },
  { label: 'Noom', x: 18, y: 58, type: 'competitor' },
  { label: 'MyFitnessPal', x: 12, y: 10, type: 'competitor' },
];

const PACKAGE_COLORS = ['#14B8A6', '#F59E0B', '#8B5CF6'];
const PERSONALIZATION_FEATURES = ['Personalized Wellness Plan', 'DNA Health Report', 'Nutrition Tracking'];

function getPackagePosition(config: PackageConfig, index: number): { x: number; y: number } {
  const featureCount = config.enabledFeatures.length;
  const x = Math.min(90, 18 + (featureCount / 7) * 70);

  const persCount = config.enabledFeatures.filter(f => PERSONALIZATION_FEATURES.includes(f)).length;
  let y = 15 + (persCount / PERSONALIZATION_FEATURES.length) * 68;
  y += index * 10;
  return { x: Math.min(90, x), y: Math.min(88, y) };
}

export function CompetitivePositioningMap() {
  const packages = useSimulatorStore(s => s.packages);

  const allPoints = useMemo(() => {
    const pkgPoints: MapPoint[] = packages.map((config, i) => ({
      label: `${config.name} ($${config.price.toLocaleString()})`,
      ...getPackagePosition(config, i),
      type: 'package' as const,
      color: PACKAGE_COLORS[i],
    }));
    return [...COMPETITORS, ...pkgPoints];
  }, [packages]);

  return (
    <div className="card mb-6">
      <div className="flex items-center gap-2 mb-1">
        <h3 className="text-heading font-semibold text-secondary uppercase tracking-wider">
          Competitive Positioning Map
        </h3>
        <InfoButton helpKey="competitivePositioning" size={13} />
      </div>
      <p className="text-sm text-muted mb-4">Directional estimate. X-axis: feature breadth. Y-axis: personalization depth. Competitor positions are approximate.</p>

      <div className="relative mx-auto border border-border rounded-lg bg-surface/30" style={{ height: '320px', maxWidth: '520px' }}>
        {/* Quadrant labels */}
        <div className="absolute top-1.5 left-2 text-[8px] text-muted/40">Generic</div>
        <div className="absolute bottom-1.5 left-2 text-[8px] text-muted/40">Personalized</div>
        <div className="absolute top-1.5 right-2 text-[8px] text-muted/40">Comprehensive</div>
        <div className="absolute bottom-1.5 right-2 text-[8px] text-muted/40">Comprehensive + Personalized</div>

        {/* Axis lines */}
        <div className="absolute top-0 bottom-0 left-1/2 w-px bg-border/40" />
        <div className="absolute left-0 right-0 top-1/2 h-px bg-border/40" />

        {/* Points */}
        {allPoints.map((pt, i) => {
          const isPackage = pt.type === 'package';
          const onRight = pt.x > 55;
          return (
            <div
              key={i}
              className="absolute flex items-center gap-1"
              style={{
                left: `${pt.x}%`,
                bottom: `${pt.y}%`,
                transform: onRight ? 'translate(-100%, 50%)' : 'translate(0%, 50%)',
                flexDirection: onRight ? 'row-reverse' : 'row',
              }}
            >
              <div
                className={`rounded-full shrink-0 ${isPackage ? 'w-3.5 h-3.5 border-2 border-white shadow-sm' : 'w-2 h-2'}`}
                style={{ backgroundColor: isPackage ? (pt.color ?? '#14B8A6') : '#9CA3AF' }}
              />
              <span className={`whitespace-nowrap leading-none ${isPackage ? 'text-micro font-semibold text-primary' : 'text-[8px] text-muted'}`}>
                {pt.label}
              </span>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 mt-3 justify-center">
        {packages.map((config, i) => (
          <div key={i} className="flex items-center gap-1 text-micro">
            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: PACKAGE_COLORS[i] }} />
            <span className="text-secondary font-medium">{config.name}</span>
          </div>
        ))}
        <div className="flex items-center gap-1 text-micro">
          <div className="w-2 h-2 rounded-full bg-gray-400" />
          <span className="text-muted">Competitors</span>
        </div>
      </div>

      <p className="text-micro text-muted italic mt-2 text-center">
        X: wellness dimensions covered. Y: personalization/adaptation. Competitor positions are directional estimates from respondent mentions.
      </p>
    </div>
  );
}
